import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  BOOLEAN_FIELD,
  DATE_FIELD,
  DATETIME_FIELD,
  EMAIL_FIELD,
  FILE_FIELD,
  HIDDEN_FIELD,
  LINE_FIELD,
  LINE_AMOUNT_FIELD,
  MULTI_SELECT_FIELD,
  NUMBER_FIELD,
  PARAGRAPH_FIELD,
  SELECT_FIELD,
  AUTOCOMPLETE_FIELD,
  LOCATION_FIELD,
  BIC_AUTOCOMPLETE_FIELD,
  CheckboxField,
  DropdownInputField,
  EmailInputField,
  FileInputField,
  FormFieldBase,
  HiddenInputField,
  LineInputField,
  LineAmountInputField,
  LocationInputField,
  NumberInputField,
  TextboxField,
  FieldAttribute,
  DateInputField,
  DateTimeInputField,
  AutoCompleteInputField,
  BICAutoCompleteInputField,
  EQUITY_SEARCH_SELECT,
  EquitySearchSelectOptionsInputField,
} from './models';

@Injectable({
  providedIn: 'root',
})
export class DynamicFormService {
  constructor(private fb: UntypedFormBuilder) {}

  fieldAttributeToFormField(field: FieldAttribute, index: number = 1): FormFieldBase<any> {
    const obj = { order: index };
    if (field.field_type === LINE_FIELD) {
      return new LineInputField(Object.assign(obj, field));
    } else if (field.field_type === LINE_AMOUNT_FIELD) {
      return new LineAmountInputField(Object.assign(obj, field));
    } else if (field.field_type === PARAGRAPH_FIELD) {
      return new TextboxField(Object.assign(obj, field));
    } else if (field.field_type === EMAIL_FIELD) {
      return new EmailInputField(Object.assign(obj, field));
    } else if (field.field_type === BOOLEAN_FIELD) {
      return new CheckboxField(Object.assign(obj, field));
    } else if (field.field_type === SELECT_FIELD) {
      return new DropdownInputField(Object.assign(obj, field));
    } else if (field.field_type === AUTOCOMPLETE_FIELD) {
      return new AutoCompleteInputField(Object.assign(obj, field));
    } else if (field.field_type === BIC_AUTOCOMPLETE_FIELD) {
      return new BICAutoCompleteInputField(Object.assign(obj, field));
    } else if (field.field_type === EQUITY_SEARCH_SELECT) {
      return new EquitySearchSelectOptionsInputField(Object.assign(obj, field));
    } else if (field.field_type === MULTI_SELECT_FIELD) {
      return new DropdownInputField(Object.assign(obj, field, { multiple: true, options: field.options }));
    } else if (field.field_type === DATE_FIELD) {
      return new DateInputField(Object.assign(obj, field));
    } else if (field.field_type === DATETIME_FIELD) {
      return new DateTimeInputField(Object.assign(obj, field));
    } else if (field.field_type === FILE_FIELD) {
      return new FileInputField(Object.assign(obj, field));
    } else if (field.field_type === NUMBER_FIELD) {
      return new NumberInputField(Object.assign(obj, field));
    } else if (field.field_type === HIDDEN_FIELD) {
      return new HiddenInputField(Object.assign(obj, field));
    } else if (field.field_type === LOCATION_FIELD) {
      return new LocationInputField(Object.assign(obj, field));
    }
    return new LineInputField(Object.assign(obj, field));
  }

  toFormGroup(fields: FormFieldBase<any>[]): UntypedFormGroup {
    const group: any = {};
    fields.forEach(field => {
      group[<string>field.key] = this.toFormControl(field);
    });
    fields.sort((a, b) => <number>a.order - <number>b.order);
    return this.fb.group({ ...group });
  }

  toFormControl(field: FormFieldBase<any>): UntypedFormControl {
    return new UntypedFormControl(
      { value: field.value || '', disabled: field?.disabled },
      {
        validators:
          field.validators ?? this.getValidators(field.type, field.required, field?.maximum, field?.minimum, true),
        updateOn: field.updateOn,
      }
    );
  }

  getValidators(
    type: any,
    required = false,
    maxLength: any = null,
    minLength: any = null,
    specialChar = false
  ): ValidatorFn[] {
    const validators = [];
    if (type === 'email') {
      validators.push(Validators.email);
    }
    if (required) {
      validators.push(Validators.required);
    }
    if (maxLength) {
      validators.push(Validators.maxLength(maxLength));
    }
    if (minLength) {
      validators.push(Validators.minLength(minLength));
    }
    if (specialChar && type === 'text') {
      validators.push(Validators.pattern(/^[a-zA-Z0-9\s!^*()_=\-\[\]{}|;:'",.<>/?`~]*$/));
    }
    return validators;
  }
}
